import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import ServiceTypeEditDialog from '../../components/serviceTypes/ServiceTypeEditDialog';
import MappingSuggestionsDialog from '../../components/serviceTypes/MappingSuggestionsDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };
const GRID = 'grid grid-cols-[24px_1fr_auto] md:grid-cols-[24px_28px_1fr_110px_130px_repeat(4,64px)_72px] gap-2 items-center';
const tab = (on) => `px-3.5 py-1.5 rounded-full text-xs border transition-colors ${on ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold' : 'border-[#262626] text-[#A3A3A3] hover:border-[#D4AF37]/60'}`;
const blank = (department_id = '', division_id = '') => ({ name: '', department_id, division_id, customer_required: true, revenue_outcome_required: true, track_time: true, active: true });

// iter317 — Service Types organized Trade → Division → Service Types.
const Tabs = ({ items, value, onChange, testid, counts }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {items.map((t) => (
      <button key={t.id} data-testid={`${testid}-${t.id}`} onClick={() => onChange(t.id)} className={tab(value === t.id)}>
        {t.name}{counts ? <span className="ml-1 opacity-70">{counts(t.id)}</span> : null}
      </button>
    ))}
  </div>
);

const Row = ({ s, checked, onCheck, onEdit, onToggle, onDisable, canMove, onMove }) => (
  <div data-testid={`st-row-${s.id}`} className={`${GRID} px-3 py-1.5 border-t border-[#262626] text-sm ${s.active ? '' : 'opacity-50'}`}>
    <input type="checkbox" data-testid={`st-check-${s.id}`} checked={checked} onChange={(e) => onCheck(e.target.checked)} />
    <div className="hidden md:flex flex-col">
      <button data-testid={`st-up-${s.id}`} disabled={!canMove} onClick={() => onMove(-1)} className="text-[#525252] hover:text-white disabled:opacity-20"><ChevronUp className="w-3.5 h-3.5" /></button>
      <button data-testid={`st-down-${s.id}`} disabled={!canMove} onClick={() => onMove(1)} className="text-[#525252] hover:text-white disabled:opacity-20"><ChevronDown className="w-3.5 h-3.5" /></button>
    </div>
    <span data-testid={`st-name-${s.id}`} className="text-white truncate">{s.name}{s.is_test && <span className="ml-1.5 text-[9px] text-[#737373] uppercase">test</span>}</span>
    <span className={`hidden md:block text-xs truncate ${s.department_id ? 'text-[#D4D4D4]' : 'text-orange-300'}`} data-testid={`st-trade-${s.id}`}>{s.department_name || 'Unassigned'}</span>
    <span className={`hidden md:block text-xs truncate ${s.division_id ? 'text-[#D4D4D4]' : 'text-orange-300'}`} data-testid={`st-division-${s.id}`}>{s.division_name || 'Unassigned'}</span>
    {[['customer_required', 'cust-req'], ['revenue_outcome_required', 'rev-req'], ['track_time', 'track'], ['active', 'active']].map(([k, t]) => (
      <span key={k} className="hidden md:flex justify-center">
        <Switch data-testid={`st-${t}-${s.id}`} checked={!!s[k]} onCheckedChange={(v) => onToggle({ [k]: v })} className="data-[state=checked]:bg-[#D4AF37] scale-90" />
      </span>
    ))}
    <span className="flex justify-end gap-1">
      <Button data-testid={`st-edit-${s.id}`} size="sm" variant="ghost" onClick={onEdit} className="h-7 px-2 text-[#A3A3A3] hover:text-white"><Pencil className="w-3.5 h-3.5" /></Button>
      <Button data-testid={`st-delete-${s.id}`} size="sm" variant="ghost" onClick={onDisable} className="h-7 px-2 text-red-400 hover:text-white hover:bg-[#7F1D1D]"><Trash2 className="w-3.5 h-3.5" /></Button>
    </span>
  </div>
);

const ServiceTypesSettings = () => {
  const [items, setItems] = useState([]);
  const [trades, setTrades] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [trade, setTrade] = useState('all');
  const [division, setDivision] = useState('all');
  const [hideTest, setHideTest] = useState(true);
  const [checked, setChecked] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [bulk, setBulk] = useState({ department_id: '', division_id: '' });

  const load = useCallback(async () => {
    try {
      const [s, t, d] = await Promise.all([
        axios.get(`${API}/admin/service-types?include_inactive=true`, opts),
        axios.get(`${API}/inv/departments`, opts),
        axios.get(`${API}/divisions?include_inactive=true`, opts),
      ]);
      setItems(s.data); setTrades(t.data.filter((x) => x.active !== false)); setDivisions(d.data);
    } catch { toast.error('Failed to load service types'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => items.filter((s) => !(hideTest && s.is_test)), [items, hideTest]);
  const inTrade = (s, t) => (t === 'all' ? true : t === 'unassigned' ? !s.department_id : s.department_id === t);
  const inDiv = (s, d) => (d === 'all' ? true : d === 'unassigned' ? !s.division_id : s.division_id === d);
  const filtered = useMemo(() => visible.filter((s) => inTrade(s, trade) && inDiv(s, division)), [visible, trade, division]);
  const tradeTabs = [{ id: 'all', name: 'All' }, ...trades.map((t) => ({ id: t.id, name: t.name })), { id: 'unassigned', name: 'Unassigned' }];
  const divTabs = trade === 'all' || trade === 'unassigned' ? [] : [{ id: 'all', name: 'All' }, ...divisions.filter((d) => d.trade_id === trade && d.active).map((d) => ({ id: d.id, name: d.name })), { id: 'unassigned', name: 'Unassigned' }];
  const unmapped = visible.filter((s) => !s.division_id).length;
  const canMove = trade === 'all' && division === 'all';

  const pickTrade = (t) => { setTrade(t); setDivision('all'); setChecked(new Set()); };
  const patch = async (id, p) => {
    try { await axios.patch(`${API}/admin/service-types/${id}`, p, opts); await load(); } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  const saveEdit = async (f) => {
    const body = { name: f.name, department_id: f.department_id || '', division_id: f.division_id || '', customer_required: f.customer_required, revenue_outcome_required: f.revenue_outcome_required, track_time: f.track_time, active: f.active };
    try {
      if (f.id) await axios.patch(`${API}/admin/service-types/${f.id}`, body, opts);
      else await axios.post(`${API}/admin/service-types`, { ...body, order: items.length }, opts);
      toast.success(f.id ? 'Saved' : `Added "${f.name}"`); setEditing(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const disable = async (id) => {
    try { await axios.delete(`${API}/admin/service-types/${id}`, opts); toast.success('Disabled'); load(); } catch { toast.error('Failed'); }
  };
  const move = async (s, dir) => {
    const idx = items.indexOf(s); const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items]; [next[idx], next[j]] = [next[j], next[idx]]; setItems(next);
    try { await axios.post(`${API}/admin/service-types/reorder`, { ids: next.map((x) => x.id) }, opts); } catch { toast.error('Reorder failed'); load(); }
  };
  const bulkAssign = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/service-types/bulk-assign`, { ids: [...checked], ...bulk }, opts);
      toast.success(`Updated ${data.updated} service types`); setChecked(new Set()); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk assign failed'); }
  };
  const allChecked = filtered.length > 0 && filtered.every((s) => checked.has(s.id));
  const addDefaults = blank(trades.some((t) => t.id === trade) ? trade : '', divisions.some((d) => d.id === division) ? division : '');

  return (
    <div className="space-y-5" data-testid="service-types-settings">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1">Service Types</h2>
          <p className="text-[#A3A3A3] text-sm max-w-2xl">Organized Trade → Division → Service Type. New Shift only offers the service types of the chosen Trade + Division. <Link to="/admin/divisions" className="text-[#D4AF37] hover:underline" data-testid="st-manage-divisions-link">Manage Divisions</Link> · <Link to="/admin/departments" className="text-[#D4AF37] hover:underline" data-testid="st-manage-departments-link">Manage Trades</Link></p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="st-open-suggestions" variant="outline" onClick={() => setSuggestOpen(true)} className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"><Sparkles className="w-4 h-4 mr-1" /> Suggested mapping{unmapped ? ` (${unmapped})` : ''}</Button>
          <Button data-testid="st-add" onClick={() => setEditing(addDefaults)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-1" /> Add Service Type</Button>
        </div>
      </div>

      <Tabs testid="st-trade-tabs" items={tradeTabs} value={trade} onChange={pickTrade} counts={(id) => visible.filter((s) => inTrade(s, id)).length} />
      {divTabs.length > 0 && <Tabs testid="st-division-tabs" items={divTabs} value={division} onChange={(d) => { setDivision(d); setChecked(new Set()); }} counts={(id) => visible.filter((s) => inTrade(s, trade) && inDiv(s, id)).length} />}

      <div className="flex flex-wrap items-center gap-3" data-testid="st-filters">
        <label className="flex items-center gap-2 text-white text-xs"><Switch data-testid="st-hide-test" checked={hideTest} onCheckedChange={setHideTest} className="data-[state=checked]:bg-[#D4AF37]" /> Hide Test Types</label>
        <span className="text-[#525252] text-xs" data-testid="st-count">{filtered.length} shown · {unmapped} not yet mapped{canMove ? '' : ' · reorder from All'}</span>
      </div>

      {checked.size > 0 && (
        <div data-testid="st-bulk-bar" className="flex flex-wrap items-center gap-2 rounded-md border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-3 py-2 text-sm">
          <span className="text-white font-semibold">{checked.size} selected → assign to</span>
          <select data-testid="st-bulk-trade" value={bulk.department_id} onChange={(e) => setBulk({ department_id: e.target.value, division_id: '' })} className="h-8 rounded bg-[#0A0A0A] border border-[#262626] text-white text-xs px-2">
            <option value="">Unassigned</option>
            {trades.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select data-testid="st-bulk-division" value={bulk.division_id} disabled={!bulk.department_id} onChange={(e) => setBulk({ ...bulk, division_id: e.target.value })} className="h-8 rounded bg-[#0A0A0A] border border-[#262626] text-white text-xs px-2 disabled:opacity-50">
            <option value="">Unassigned</option>
            {divisions.filter((d) => d.trade_id === bulk.department_id && d.active).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <Button data-testid="st-bulk-apply" size="sm" onClick={bulkAssign} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">Assign</Button>
          <Button data-testid="st-bulk-clear" size="sm" variant="ghost" onClick={() => setChecked(new Set())} className="text-[#A3A3A3]">Clear</Button>
        </div>
      )}

      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-hidden" data-testid="st-list">
        <div className={`${GRID} hidden md:grid px-3 py-2 bg-[#0A0A0A] text-[#737373] text-[10px] uppercase tracking-wider`}>
          <input type="checkbox" data-testid="st-check-all" checked={allChecked} onChange={(e) => setChecked(e.target.checked ? new Set(filtered.map((s) => s.id)) : new Set())} />
          <span /><span>Name</span><span>Trade</span><span>Division</span>
          <span className="text-center">Cust Req</span><span className="text-center">Rev Outcome</span><span className="text-center">Track Time</span><span className="text-center">Active</span><span />
        </div>
        {filtered.length === 0 && <div className="p-6 text-center text-[#A3A3A3] text-sm" data-testid="st-empty">No service types in this view.</div>}
        {filtered.map((s) => (
          <Row key={s.id} s={s} checked={checked.has(s.id)} canMove={canMove}
            onCheck={(v) => setChecked((p) => { const n = new Set(p); if (v) n.add(s.id); else n.delete(s.id); return n; })}
            onEdit={() => setEditing({ ...s })} onToggle={(p) => patch(s.id, p)} onDisable={() => disable(s.id)} onMove={(dir) => move(s, dir)} />
        ))}
      </div>

      <ServiceTypeEditDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} initial={editing} trades={trades} divisions={divisions} onSave={saveEdit} />
      <MappingSuggestionsDialog open={suggestOpen} onOpenChange={setSuggestOpen} trades={trades} divisions={divisions} hideTest={hideTest} onApplied={load} />
    </div>
  );
};

export default ServiceTypesSettings;
